import {getTestProps} from '@benchling/js-test-utils';
import {css} from 'aphrodite';
import React from 'react';
import {Handle, Position, ReactFlowInstance} from 'reactflow';

import {AddNodeDropdown} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/AddNodeDropdown';
import {
  HANDLE_TYPE_FIELD,
  HANDLE_DIRECTION_SOURCE,
  HANDLE_DIRECTION_TARGET,
  HandleDirection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {GetAddNodeOptionsForRightHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import AddNodeHandle, {AddNodeHandleProps} from 'ui-library/reactflow/handles/AddNodeHandle';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {NODE_HEADER_HANDLE_STYLES} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';

import {EdgesContext} from '../EdgesContext';
import {FlowchartEditorModeContext} from '../FlowchartEditorModeContext';
import {HandlesContext, HandlesContextValue} from '../HandlesContext';
import {FlowchartEditorMode} from '../ProcessFlowchartEditorModeConstants';

function isDraggingFromFieldHandle(connectionDragState: HandlesContextValue['connectionDragState']): boolean {
  return (
    connectionDragState?.isDragging === true &&
    connectionDragState.sourceHandleInfo?.handleCategory === HANDLE_TYPE_FIELD
  );
}

function useIsHandleConnected(handleId: string, type: HandleDirection): boolean {
  const edgesContext = EdgesContext.useContext();
  return edgesContext.edges.some((edge) =>
    type === HANDLE_DIRECTION_TARGET ? edge.targetHandle === handleId : edge.sourceHandle === handleId
  );
}

/**
 * Context-aware handle component that only re-renders when drag state changes,
 * without causing parent node to re-render
 */
function ContextAwareNodeHandleComponent({
  id,
  type,
  position,
  testProps,
}: {
  id: string;
  type: HandleDirection;
  position: Position;
  testProps: ReturnType<typeof getTestProps>;
}): JSX.Element | null {
  const handlesContext = HandlesContext.useContext();
  const editorMode = FlowchartEditorModeContext.useContext();
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const isConnected = useIsHandleConnected(id, type);
  const hideForViewer = locked || (editorMode === FlowchartEditorMode.EXECUTION_VIEWER && !isConnected);

  if (isDraggingFromFieldHandle(handlesContext.connectionDragState)) {
    return null;
  }

  return (
    <Handle
      id={id}
      type={type}
      position={position}
      className={css(
        NODE_HEADER_HANDLE_STYLES.nodeHandle,
        type === 'target' ? NODE_HEADER_HANDLE_STYLES.leftHandle : NODE_HEADER_HANDLE_STYLES.rightHandle,
        hideForViewer && NODE_HEADER_HANDLE_STYLES.lockedNodeHandle // Use CSS to hide (keeps handle in DOM for ReactFlow layout)
      )}
      {...testProps}
    >
      <div
        className={css(
          NODE_HEADER_HANDLE_STYLES.handleDot,
          hideForViewer && NODE_HEADER_HANDLE_STYLES.hidden
        )}
      />
    </Handle>
  );
}

const ContextAwareNodeHandle = React.memo(ContextAwareNodeHandleComponent);

/**
 * Context-aware AddNodeHandle that hides when dragging from field handle
 */
function ContextAwareAddNodeHandleComponent(props: {
  id: string;
  type: typeof HANDLE_DIRECTION_SOURCE;
  isConnectable: boolean;
  renderPopoverContent?: () => JSX.Element;
  onClick?: () => void;
}): JSX.Element | null {
  const handlesContext = HandlesContext.useContext();
  const editorMode = FlowchartEditorModeContext.useContext();
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const isConnected = useIsHandleConnected(props.id, props.type);
  const hideForViewer = locked || (editorMode === FlowchartEditorMode.EXECUTION_VIEWER && !isConnected);

  if (isDraggingFromFieldHandle(handlesContext.connectionDragState)) {
    return null;
  }

  // Use CSS to hide when locked or hidden for viewer (keeps handle in DOM for ReactFlow layout)
  if (hideForViewer) {
    return (
      <span style={{visibility: 'hidden', pointerEvents: 'none'}}>
        <AddNodeHandle {...props} />
      </span>
    );
  }

  return <AddNodeHandle {...props} />;
}

const ContextAwareAddNodeHandle = React.memo(ContextAwareAddNodeHandleComponent);

export default function createHeaderPropsForNode(
  nodeData: {
    id: string;
    nodeName: string;
    nodeIcon?: BenchlingIconName;
    nodeSecondaryInfo?: string;
    nodeType: ProcessFlowchartNodeType;
    position: {x: number; y: number};
    getAddNodeOptionsForRightHandle?: GetAddNodeOptionsForRightHandle;
  },
  reactFlowInstance: ReactFlowInstance,
  hasLeftHandle: boolean = true,
  hasRightHandle: boolean = true,
  isDisabled: boolean = false
): {
  title: string;
  subTitle?: string;
  leftIcon?: BenchlingIconName;
  leftHandle?: React.ReactNode;
  rightHandle?: React.ReactNode;
} {
  const {id, nodeName, nodeIcon, nodeSecondaryInfo} = nodeData;
  const addNodesOptions = nodeData.getAddNodeOptionsForRightHandle?.(nodeData.id, reactFlowInstance);
  const addNodeHandleProps: Pick<AddNodeHandleProps, 'renderPopoverContent' | 'onClick' | 'disabled'> = {
    disabled: isDisabled,
  };
  if (addNodesOptions != null) {
    // If there is exactly one option, we perform that action on click instead of showing a popover menu.
    if (addNodesOptions.length === 1) {
      addNodeHandleProps.onClick = addNodesOptions[0].onSelect;
    } else if (addNodesOptions.length > 1) {
      addNodeHandleProps.renderPopoverContent = () => <AddNodeDropdown options={addNodesOptions} />;
    }
  }

  return {
    title: nodeName,
    subTitle: nodeSecondaryInfo,
    leftIcon: nodeIcon,
    leftHandle: hasLeftHandle ? (
      <ContextAwareNodeHandle
        id={defaultNodeHandleIdGenerator(id, HANDLE_DIRECTION_TARGET)}
        type={HANDLE_DIRECTION_TARGET}
        position={Position.Left}
        testProps={getTestProps({
          component: 'ProcessFlowchartNode',
          element: 'targetHandle',
          key: nodeName,
        })}
      />
    ) : null,
    rightHandle: hasRightHandle ? (
      <ContextAwareAddNodeHandle
        id={defaultNodeHandleIdGenerator(id, HANDLE_DIRECTION_SOURCE)}
        type={HANDLE_DIRECTION_SOURCE}
        isConnectable={true}
        {...addNodeHandleProps}
      />
    ) : null,
  };
}
